import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { VendorInvitationStatusBadge } from './VendorInvitationStatusBadge';
import { PlatformVendorInvitation } from '@/lib/types/vendorInvitation';
import { format } from 'date-fns';
import { Ban, RefreshCw, Search } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface VendorInvitationsTableProps {
  invitations: PlatformVendorInvitation[];
  onCancel: (invitationId: string) => void;
  onReInvite: (email: string, message: string | null) => void;
}

export const VendorInvitationsTable: React.FC<VendorInvitationsTableProps> = ({
  invitations,
  onCancel,
  onReInvite,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredInvitations = invitations.filter(inv =>
    inv.invited_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const truncateMessage = (message: string | null) => {
    if (!message) return '-';
    if (message.length <= 50) return message;
    return message.substring(0, 50) + '...';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Message</TableHead>
              <TableHead>Sent Date</TableHead>
              <TableHead>Accepted Date</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInvitations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No invitations found
                </TableCell>
              </TableRow>
            ) : (
              filteredInvitations.map((invitation) => (
                <TableRow key={invitation.id}>
                  <TableCell className="font-medium">{invitation.invited_email}</TableCell>
                  <TableCell>
                    <VendorInvitationStatusBadge status={invitation.status} />
                  </TableCell>
                  <TableCell>
                    {invitation.personal_message ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="cursor-help text-muted-foreground">
                              {truncateMessage(invitation.personal_message)}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-sm">
                            <p className="whitespace-pre-wrap">{invitation.personal_message}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {format(new Date(invitation.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invitation.accepted_at
                      ? format(new Date(invitation.accepted_at), 'MMM d, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {invitation.status === 'pending'
                      ? format(new Date(invitation.expires_at), 'MMM d, yyyy')
                      : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {invitation.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onCancel(invitation.id)}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                      {invitation.status === 'expired' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onReInvite(invitation.invited_email, invitation.personal_message)}
                        >
                          <RefreshCw className="h-4 w-4 mr-1" />
                          Re-invite
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
